import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Menu, X, ChevronRight } from "lucide-react";

// Marketplace hidden until feature is closer to live (per v5 spec)
const navLinks = [
  { name: "Product", path: "/product" },
  { name: "Use Cases", path: "/use-cases" },
  { name: "Research", path: "/whitepapers" },
  { name: "Docs", path: "/docs" },
  { name: "About", path: "/about" },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-swarm-darker/80 backdrop-blur-xl border-b border-swarm-border"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/jsghs2ap_swarmcitadel-horizontal-primary-dark-bg-2172x724.png"
                alt="SwarmCitadel"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    location.pathname === link.path
                      ? "text-swarm-cyan bg-swarm-cyan/10"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/investors">
                <Button
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/5"
                  data-testid="nav-investors-btn"
                  data-magnetic
                >
                  Investors
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  className="bg-swarm-violet hover:bg-swarm-violet/90 text-white px-6"
                  data-testid="nav-demo-btn"
                  data-magnetic
                >
                  Request Demo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-20 left-0 right-0 bg-swarm-darker border-b border-swarm-border p-6 animate-slide-in-up">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? "text-swarm-cyan bg-swarm-cyan/10"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <hr className="border-swarm-border my-2" />
              <Link
                to="/investors"
                className="px-4 py-3 text-base font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5"
              >
                Investors
              </Link>
              <Link to="/contact">
                <Button className="w-full mt-2 bg-swarm-violet hover:bg-swarm-violet/90 text-white">
                  Request Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
